import type { JsonRecord, JsonValue, VadiumMessage } from "@/lib/api";
import { startSseTransport, type SseHandle, type SseReconnect } from "@/lib/sseTransport";

export type StreamEventType =
  | "chat_id"
  | "content_delta"
  | "content_done"
  | "reasoning_delta"
  | "reasoning_block_end"
  | "tool_call_start"
  | "doc_read_start"
  | "doc_read"
  | "doc_find_start"
  | "doc_find"
  | "doc_created_start"
  | "doc_created"
  | "doc_download"
  | "doc_replicate_start"
  | "doc_replicated"
  | "doc_edited_start"
  | "doc_edited"
  | "web_search_start"
  | "web_search_done"
  | "corpus_search_start"
  | "corpus_search_done"
  | "workflow_started"
  | "workflow_phase"
  | "task_started"
  | "task_done"
  | "task_retry"
  | "task_refuted"
  | "quote_check"
  | "workflow_progress"
  | "workflow_retry"
  | "workflow_done"
  | "workflow_budget_exhausted"
  | "turn_failed"
  | "research_report"
  | "citations"
  | "review_started"
  | "claim_verdict"
  | "redraft_started"
  | "answer_revised"
  | "review_done"
  | "turn_done"
  | "turn_meta";

export interface SpanLocator extends JsonRecord {
  kind?: string;
  page?: number;
  char_start?: number;
  char_end?: number;
  para_index?: number;
}

export interface Span extends JsonRecord {
  doc_id?: string;
  version_id?: string;
  version_number?: number;
  filename?: string;
  page?: number;
  locator?: SpanLocator;
  quote?: string;
  chunk_id?: string;
}

export interface VerificationResult extends JsonRecord {
  status?: "verified" | "unverified" | "unchecked";
  fidelity?: "exact" | "paraphrase" | "missing";
  judged?: boolean;
}

export interface CitationObject extends JsonRecord {
  kind?: "doc_citation";
  ref?: number;
  doc_id?: string;
  document_id?: string;
  filename?: string;
  version_id?: string;
  version_number?: number | null;
  page?: string | number;
  quote?: string;
  spans?: Span[];
  verification?: VerificationResult;
  /** Tier-1 lexical guard (Spec 1a): verbatim quote found at the cited page. */
  lexical_ok?: boolean;
  /** Failure reason when `lexical_ok` is false. */
  reason?: string;
}

export interface WebCitationObject extends JsonRecord {
  kind: "web_citation";
  ref?: number;
  url?: string;
  title?: string;
  snippet?: string;
  retrieved_at?: string;
}

export function isWebCitation(c: CitationObject | WebCitationObject): c is WebCitationObject {
  const record = c as Record<string, unknown>;
  if (record.kind === "web_citation") return true;
  return typeof record.url === "string" && !!record.url;
}

/**
 * Narrow to a document citation. Checks `kind === "doc_citation"` (v2 events)
 * with a fallback to `doc_id` presence for pre-v2 persisted events that lack `kind`.
 */
export function isDocCitation(c: CitationObject | WebCitationObject): c is CitationObject {
  return c.kind === "doc_citation" || (!isWebCitation(c) && typeof c.doc_id === "string");
}

export interface EditAnnotation extends JsonRecord {
  kind: "edit";
  edit_id: string;
  document_id: string;
  version_id: string;
  version_number: number | null;
  change_id: string;
  del_w_id?: string | null;
  ins_w_id?: string | null;
  deleted_text?: string;
  inserted_text?: string;
  context_before?: string;
  context_after?: string;
  reason?: string;
  status?: "pending" | "accepted" | "rejected";
}

export type TaskRetryReason = "non_verbatim_quotes" | "refuted" | "cached_refute";

export type ChatStreamEvent =
  | { type: "chat_id"; chat_id: string; chatId: string }
  | { type: "content_delta"; text: string }
  | { type: "content_done" }
  | { type: "reasoning_delta"; text: string }
  | { type: "reasoning_block_end" }
  | { type: "tool_call_start"; name: string }
  | { type: "doc_read_start"; filename: string; document_id: string | null }
  | { type: "doc_read"; filename: string; document_id: string | null }
  | { type: "doc_find_start"; filename: string; query: string }
  | { type: "doc_find"; filename: string; query: string; total_matches: number }
  | { type: "doc_created_start"; filename: string }
  | { type: "doc_created"; filename: string; download_url: string; document_id: string; version_id: string; version_number: number | null }
  | { type: "doc_download"; filename: string; download_url: string }
  | { type: "doc_replicate_start"; filename: string; count: number }
  | {
      type: "doc_replicated";
      filename: string;
      count: number;
      copies?: Array<{ new_filename: string; document_id: string; version_id: string }>;
      error?: string;
    }
  | { type: "doc_edited_start"; filename: string }
  | {
      type: "doc_edited";
      filename: string;
      document_id?: string;
      version_id?: string;
      version_number?: number | null;
      download_url?: string;
      annotations?: EditAnnotation[];
      error?: string;
    }
  | { type: "web_search_start"; query: string }
  | { type: "web_search_done"; query: string; num_results: number }
  | { type: "corpus_search_start"; query: string }
  | { type: "corpus_search_done"; query: string; num_results: number }
  | { type: "workflow_started"; run_id: string; name: string; num_tasks: number }
  | { type: "workflow_phase"; task_id: string; agent: string; fan_out: string | null; count: number; phase?: string; label?: string }
  | { type: "task_started"; task_id: string; item: string | null }
  | { type: "task_done"; task_id: string; item: string | null; cached: boolean }
  | { type: "task_retry"; task_id: string; item: string | null; reason: TaskRetryReason }
  | { type: "task_refuted"; task_id: string; item: string | null }
  | { type: "quote_check"; task_id: string; item: string | null; checked: number; repaired: number; unrepaired: number; retry?: boolean }
  | { type: "workflow_progress"; completed: number; kept: number; started: number; phase?: string; label?: string; total?: number }
  | { type: "workflow_retry"; attempt: number; max_attempts: number; reason: string }
  | { type: "workflow_done"; run_id: string; kept: number; completed: number; refuted: number }
  | { type: "workflow_budget_exhausted"; started: number; max_tasks: number }
  | { type: "turn_failed"; reason: string; message: string }
  | { type: "research_report"; report: Record<string, unknown> }
  | { type: "citations"; citations: Array<CitationObject | WebCitationObject> }
  | { type: "review_started"; claim_count: number }
  | { type: "claim_verdict"; ref: number; verdict: string; confidence: number }
  | { type: "redraft_started"; iteration: number; flagged_refs: number[] }
  | { type: "answer_revised"; content: string }
  | { type: "review_done"; checked: number; corrected: number; residual_flags: ResidualFlag[] }
  | { type: "turn_done"; duration_ms: number }
  | { type: "turn_meta"; turn_id: string; chat_id: string; id: string };

export interface ResidualFlag extends JsonRecord {
  ref: number;
  verdict: string;
  reason: string;
}

export type StreamDispatchEvent = ChatStreamEvent | { type: "error"; message: string };

/** The next request to resume from when a stream closes before `[DONE]`. */
export type StreamReconnect = SseReconnect;

export interface StreamRequestOptions {
  path: string;
  /** HTTP method for the first request. Defaults to `POST`. */
  method?: "GET" | "POST";
  body?: unknown;
  /** `eventId` is the `chat_events` id from the SSE `id:` frame, when present. */
  onEvent: (event: StreamDispatchEvent, eventId?: string) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
  /**
   * Called when a connection drops before `[DONE]` and a reconnect is about to be
   * attempted (`reconnecting=true`), and again once a frame is received on the
   * resumed connection (`reconnecting=false`). Lets the UI surface a recoverable
   * "reconnecting…" state instead of leaving a spinner hung.
   */
  onReconnecting?: (reconnecting: boolean) => void;
  signal?: AbortSignal;
  /** Initial `Last-Event-ID` (resume cursor) sent on the first request. */
  lastEventId?: string | null;
  /**
   * Called when the stream closes (clean close or transient error) before `[DONE]`.
   * Return the next request to resume from — the latest seen id is sent as the
   * `Last-Event-ID` header — or `null` to stop without reconnecting. This is what
   * makes the live tail resumable across navigation, reload, and dropped connections.
   */
  resolveReconnect?: (lastEventId: string | null) => StreamReconnect | null;
}

export type StreamHandle = SseHandle;

// Note: corpus-review workflows require a project context, so `workflow_enabled`
// lives only on ProjectChatStreamBody, not here.
export interface ChatStreamBody {
  messages: VadiumMessage[];
  chat_id?: string | null;
  project_id?: string | null;
  web_search_enabled?: boolean;
}

export interface ProjectChatStreamBody {
  messages: VadiumMessage[];
  chat_id?: string | null;
  displayed_doc?: { filename: string; document_id: string };
  attached_documents?: Array<{ filename: string; document_id: string }>;
  web_search_enabled?: boolean;
  workflow_enabled?: boolean;
}

function asRecord(value: unknown): JsonRecord | null {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as JsonRecord;
  }

  return null;
}

function asString(value: JsonValue | undefined, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: JsonValue | undefined) {
  return typeof value === "string" ? value : null;
}

function isTaskRetryReason(value: JsonValue | undefined): value is TaskRetryReason {
  return value === "non_verbatim_quotes" || value === "refuted" || value === "cached_refute";
}

function asNumber(value: JsonValue | undefined, fallback = 0) {
  return typeof value === "number" ? value : fallback;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeEvent(raw: unknown): StreamDispatchEvent | null {
  const event = asRecord(raw);
  if (!event || typeof event.type !== "string") {
    return null;
  }

  switch (event.type) {
    case "chat_id": {
      const chatId = asString(event.chat_id) || asString(event.chatId);
      return chatId && UUID_RE.test(chatId) ? { type: "chat_id", chat_id: chatId, chatId } : null;
    }
    case "content_delta":
      return { type: "content_delta", text: asString(event.text) };
    case "content_done":
      return { type: "content_done" };
    case "reasoning_delta":
      return { type: "reasoning_delta", text: asString(event.text) };
    case "reasoning_block_end":
      return { type: "reasoning_block_end" };
    case "tool_call_start":
      return { type: "tool_call_start", name: asString(event.name) };
    case "doc_read_start":
      return { type: "doc_read_start", filename: asString(event.filename), document_id: asNullableString(event.document_id) };
    case "doc_read":
      return { type: "doc_read", filename: asString(event.filename), document_id: asNullableString(event.document_id) };
    case "doc_find_start":
      return { type: "doc_find_start", filename: asString(event.filename), query: asString(event.query) };
    case "doc_find":
      return { type: "doc_find", filename: asString(event.filename), query: asString(event.query), total_matches: asNumber(event.total_matches) };
    case "doc_created_start":
      return { type: "doc_created_start", filename: asString(event.filename) };
    case "doc_created":
      return {
        type: "doc_created",
        filename: asString(event.filename),
        download_url: asString(event.download_url),
        document_id: asString(event.document_id),
        version_id: asString(event.version_id),
        version_number: typeof event.version_number === "number" ? event.version_number : null
      };
    case "doc_download":
      return { type: "doc_download", filename: asString(event.filename), download_url: asString(event.download_url) };
    case "doc_replicate_start":
      return { type: "doc_replicate_start", filename: asString(event.filename), count: asNumber(event.count) };
    case "doc_replicated":
      return {
        type: "doc_replicated",
        filename: asString(event.filename),
        count: asNumber(event.count),
        copies: Array.isArray(event.copies) ? (event.copies as Array<{ new_filename: string; document_id: string; version_id: string }>) : undefined,
        error: asNullableString(event.error) ?? undefined
      };
    case "doc_edited_start":
      return { type: "doc_edited_start", filename: asString(event.filename) };
    case "doc_edited":
      return {
        type: "doc_edited",
        filename: asString(event.filename),
        document_id: asNullableString(event.document_id) ?? undefined,
        version_id: asNullableString(event.version_id) ?? undefined,
        version_number: typeof event.version_number === "number" ? event.version_number : null,
        download_url: asNullableString(event.download_url) ?? undefined,
        annotations: Array.isArray(event.annotations) ? (event.annotations as EditAnnotation[]) : undefined,
        error: asNullableString(event.error) ?? undefined
      };
    case "web_search_start":
      return { type: "web_search_start", query: asString(event.query) };
    case "web_search_done":
      return { type: "web_search_done", query: asString(event.query), num_results: asNumber(event.num_results) };
    case "corpus_search_start":
      return { type: "corpus_search_start", query: asString(event.query) };
    case "corpus_search_done":
      return { type: "corpus_search_done", query: asString(event.query), num_results: asNumber(event.num_results) };
    case "workflow_started":
      return { type: "workflow_started", run_id: asString(event.run_id), name: asString(event.name), num_tasks: asNumber(event.num_tasks) };
    case "workflow_phase":
      return {
        type: "workflow_phase",
        task_id: asString(event.task_id),
        agent: asString(event.agent),
        fan_out: asNullableString(event.fan_out),
        count: asNumber(event.count),
        phase: asNullableString(event.phase) ?? undefined,
        label: asNullableString(event.label) ?? undefined
      };
    case "task_started":
      return { type: "task_started", task_id: asString(event.task_id), item: asNullableString(event.item) };
    case "task_done":
      return { type: "task_done", task_id: asString(event.task_id), item: asNullableString(event.item), cached: event.cached === true };
    case "task_retry": {
      if (!isTaskRetryReason(event.reason)) return null;
      return {
        type: "task_retry",
        task_id: asString(event.task_id),
        item: asNullableString(event.item),
        reason: event.reason
      };
    }
    case "task_refuted":
      return { type: "task_refuted", task_id: asString(event.task_id), item: asNullableString(event.item) };
    case "quote_check":
      return {
        type: "quote_check",
        task_id: asString(event.task_id),
        item: asNullableString(event.item),
        checked: asNumber(event.checked),
        repaired: asNumber(event.repaired),
        unrepaired: asNumber(event.unrepaired),
        ...(typeof event.retry === "boolean" ? { retry: event.retry } : {})
      };
    case "workflow_progress":
      return {
        type: "workflow_progress",
        completed: asNumber(event.completed),
        kept: asNumber(event.kept),
        started: asNumber(event.started),
        phase: asNullableString(event.phase) ?? undefined,
        label: asNullableString(event.label) ?? undefined,
        total: typeof event.total === "number" ? event.total : undefined
      };
    case "workflow_retry":
      return { type: "workflow_retry", attempt: asNumber(event.attempt), max_attempts: asNumber(event.max_attempts), reason: asString(event.reason) };
    case "workflow_done":
      return { type: "workflow_done", run_id: asString(event.run_id), kept: asNumber(event.kept), completed: asNumber(event.completed), refuted: asNumber(event.refuted) };
    case "workflow_budget_exhausted":
      return { type: "workflow_budget_exhausted", started: asNumber(event.started), max_tasks: asNumber(event.max_tasks) };
    case "turn_failed":
      return { type: "turn_failed", reason: asString(event.reason), message: asString(event.message, "Something went wrong. Please try again.") };
    case "research_report": {
      const report = asRecord(event.report as JsonValue);
      return report ? { type: "research_report", report } : null;
    }
    case "turn_done":
      return { type: "turn_done", duration_ms: asNumber(event.duration_ms) };
    case "turn_meta":
      return { type: "turn_meta", turn_id: asString(event.turn_id), chat_id: asString(event.chat_id), id: asString(event.id) };
    case "citations": {
      const rawCitations = Array.isArray(event.citations) ? (event.citations as Array<CitationObject | WebCitationObject>) : [];
      const validCitations = rawCitations.filter((c) => {
        if (isWebCitation(c)) return true;
        const doc = c as CitationObject;
        return (!doc.document_id || UUID_RE.test(doc.document_id)) && (!doc.version_id || UUID_RE.test(doc.version_id));
      });
      return { type: "citations", citations: validCitations };
    }
    case "review_started":
      return { type: "review_started", claim_count: asNumber(event.claim_count) };
    case "claim_verdict":
      return { type: "claim_verdict", ref: asNumber(event.ref), verdict: asString(event.verdict), confidence: asNumber(event.confidence) };
    case "redraft_started":
      return {
        type: "redraft_started",
        iteration: asNumber(event.iteration),
        flagged_refs: Array.isArray(event.flagged_refs) ? (event.flagged_refs as number[]) : []
      };
    case "answer_revised":
      return { type: "answer_revised", content: asString(event.content) };
    case "review_done":
      return {
        type: "review_done",
        checked: asNumber(event.checked),
        corrected: asNumber(event.corrected),
        residual_flags: Array.isArray(event.residual_flags) ? (event.residual_flags as ResidualFlag[]) : []
      };
    case "error":
      return { type: "error", message: asString(event.message, "Stream error") };
    default:
      return null;
  }
}

// Resumable SSE stream: the connection/reconnect loop lives in `@/lib/sseTransport`;
// this layer only decodes `data:` payloads into normalized stream events.
export function streamSse(options: StreamRequestOptions): StreamHandle {
  const { onEvent, ...transport } = options;
  return startSseTransport({
    ...transport,
    onData: (data, eventId) => {
      const event = normalizeEvent(JSON.parse(data));
      if (event) {
        onEvent(event, eventId);
      }
    }
  });
}

export function streamChat(body: ChatStreamBody, callbacks: Omit<StreamRequestOptions, "path" | "body">) {
  return streamSse({ ...callbacks, path: "/chat", body });
}

export function streamProjectChat(projectId: string, body: ProjectChatStreamBody, callbacks: Omit<StreamRequestOptions, "path" | "body">) {
  return streamSse({ ...callbacks, path: `/projects/${projectId}/chat`, body });
}

// Subscribe to the resumable live tail of an in-flight turn (GET /chat/{id}/live).
// Defaults to reconnecting to the same /live endpoint with the latest seen id as cursor.
export function streamChatLive(chatId: string, callbacks: Omit<StreamRequestOptions, "path" | "body" | "method">) {
  return streamSse({
    ...callbacks,
    path: `/chat/${chatId}/live`,
    method: "GET",
    resolveReconnect: callbacks.resolveReconnect ?? (() => ({ path: `/chat/${chatId}/live`, method: "GET" }))
  });
}
